import type { ApolloCache } from '@apollo/client';

import type { DocumentType } from '@/gql';

import { FILE_MANAGER_QUERY } from '../components/FileManager';
import type { FILE_FRAGMENT } from '../fragments/fileFields';

export const addNewFileToCache = (
  cache: ApolloCache,
  newFile: DocumentType<typeof FILE_FRAGMENT> | null | undefined,
  rootOwnerId: string,
) => {
  if (!newFile) {
    return;
  }

  cache.updateQuery(
    {
      query: FILE_MANAGER_QUERY,
      variables: {
        fileId: newFile.parent?._id ?? null,
        rootOwnerId: rootOwnerId ?? '',
        includeSharingGroups: newFile.owner?.__typename === 'Service',
      },
    },
    (data) => {
      if (!data) {
        return;
      }

      const files = data.filesForOwner.filesConnection?.edges?.map(
        (edge) => edge.node,
      );

      if (!files) {
        return;
      }

      if (files.some((file) => file._id === newFile._id)) {
        return;
      }

      const newFiles = [...files, newFile];

      return {
        ...data,
        filesForOwner: {
          ...data.filesForOwner,
          __typename: 'FilesForOwner' as const,
          filesConnection: {
            ...data.filesForOwner.filesConnection,
            edges: newFiles.map((file) => ({
              __typename: 'FilesForOwnerEdge' as const,
              node: file,
            })),
            __typename: 'FilesForOwnerConnection' as const,
          },
        },
      };
    },
  );
};

export const removeFileFromCache = (
  cache: ApolloCache,
  file: DocumentType<typeof FILE_FRAGMENT> | null | undefined,
  rootOwnerId: string,
) => {
  if (!file) {
    return;
  }

  cache.updateQuery(
    {
      query: FILE_MANAGER_QUERY,
      variables: {
        fileId: file.parent?._id ?? null,
        rootOwnerId,
        includeSharingGroups: file.owner?.__typename === 'Service',
      },
    },
    (data) => {
      if (!data) {
        return;
      }

      const files = data.filesForOwner.filesConnection?.edges?.map(
        (edge) => edge.node,
      );

      if (!files) {
        return;
      }

      const newFiles = files.filter((f) => f._id !== file._id);

      return {
        ...data,
        filesForOwner: {
          ...data.filesForOwner,
          __typename: 'FilesForOwner' as const,
          filesConnection: {
            ...data.filesForOwner.filesConnection,
            edges: newFiles.map((f) => ({
              __typename: 'FilesForOwnerEdge' as const,
              node: f,
            })),
            __typename: 'FilesForOwnerConnection' as const,
          },
        },
      };
    },
  );
};
